import * as React from 'react';
import { BodyCell } from './BodyCell';
import { ColumnHeadersContext, DataGrid } from './DataGrid';
import { DataGridColors } from './DataGridColors';
import { GridSelection } from './GridSelection';
import { RowHeaderCell } from './RowHeaderCell';
import { Dictionary } from './Utils';

export class BodyRow extends React.PureComponent<BodyRow.Props> {

  public static displayName: string = 'DataGridBodyRow';

  private isCellSelected(cell: DataGrid.Cell): boolean {
    return !!this.props.selectionModel && this.props.selectionModel.isSelected(cell);
  }

  private renderCells(): React.ReactElement[] {
    const columnWidths = this.props.columnWidths || {};

    const cells: React.ReactElement<RowHeaderCell.Props | BodyCell.Props>[] = [];

    if (!!this.props.onRowHeaderCellClick && this.props.colStartIdx === 0) {
      cells.push(
        <RowHeaderCell {...{
          key: `hcell`,
          height: this.props.rowOuterHeight,
          isSelected: this.props.isSelected,
          onRowHeaderCellClick: this.handleRowHeaderCellClick,
          rowType: RowHeaderCell.RowType.Body,
          testId: 'hcell'
        }} />);
    }

    for (let colIdx = this.props.colStartIdx; colIdx <= this.props.colEndIdx; colIdx++) {
      const col = this.props.cols[colIdx];
      const shouldReportWidth = !col.preferredWidth && this.props.measuredColumnWidths[col.name] === undefined;

      cells.push(
        <BodyCell {...{
          key: col.name,
          col: col,
          row: this.props.row,
          rowIdx: this.props.rowIdx,
          colIdx: colIdx,
          value: this.props.row.data[col.dataKey],
          width: shouldReportWidth ? undefined : columnWidths[col.name],
          height: this.props.rowOuterHeight,
          reportWidthFn: shouldReportWidth ? this.props.reportWidthFn : undefined,
          isSelected: this.isCellSelected({ rowIdx: this.props.rowIdx, colIdx, rowKey: this.props.row.key, colKey: col.dataKey }),
          onCellClick: this.props.onCellClick,
          onCellDoubleClick: this.props.onCellDoubleClick,
          onCellContextMenu: this.props.onCellContextMenu,
          showVerticalGridLines: this.props.showVerticalGridLines
        }} />
      );
    }
    return cells;
  }

  public render(): JSX.Element {
    const borderBottom = this.props.isSelected ? `1px solid ${DataGridColors.SelectedGridLine}` : (this.props.showHorizontalGridLines ? `1px solid ${DataGridColors.HorizontalGridLine}` : undefined);
    const style: React.CSSProperties = {
      backgroundColor: this.getBackgroundColor(),
      borderBottom,
      boxSizing: 'border-box',
      height: this.props.rowOuterHeight,
      paddingLeft: this.props.leftMargin || 0,
      whiteSpace: 'nowrap'
    };

    return <ColumnHeadersContext.Consumer>
      {showColumnHeaders => <div {...{
        'aria-rowindex': this.props.rowIdx + (showColumnHeaders ? 2 : 1), // aria-rowindex counts from 1
        'aria-selected': this.props.isSelected,
        /**
         * 733183: "Scrolling past ~2000 rows causes a fatal rendering error"
         * The row prop can be undefined at runtime. This specifically occurs if the rowStore does not return the rows associated with the requested start
         * and end row indexes. As such, we can hit a situation where our DataGrid is expecting defined rows for row indexes that were not actually returned
         * from the rowStore. When this happens we render a styled empty BodyRow.
         */
        children: this.props.row && this.renderCells(),
        className: 'tb-react-dg-brow',
        role: 'row',
        style,
      }} />}
    </ColumnHeadersContext.Consumer>;
  }

  private getBackgroundColor = (): string | undefined => {
    const showRowBanding = this.props.showRowBanding === undefined || this.props.showRowBanding;

    if (this.props.isSelected) {
      return DataGridColors.Selected;
    } else if (/* !this.props.showHorizontalGridLines && */ showRowBanding && this.props.rowIdx % 2 === 0) {
      return DataGridColors.AltRow;
    }

    return undefined;
  }

  private handleRowHeaderCellClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!!this.props.row && !!this.props.onRowHeaderCellClick) {
      this.props.onRowHeaderCellClick(this.props.rowIdx, e);
    }
  }
}

export namespace BodyRow {

  export interface Props {
    row: DataGrid.Row;
    rowIdx: number;
    colStartIdx: number;
    colEndIdx: number;
    cols: DataGrid.Column[];
    columnWidths?: DataGrid.ColumnWidths;
    leftMargin?: number;
    rowOuterHeight: number;
    isSelected: boolean;

    showRowBanding?: boolean;
    showHorizontalGridLines?: boolean;
    showVerticalGridLines?: boolean;

    onCellClick?: DataGrid.ClickHandler;
    onCellDoubleClick?: DataGrid.ClickHandler;
    onCellContextMenu?: DataGrid.ClickHandler;

    onRowHeaderCellClick?: (rowIdx: number, e: React.MouseEvent<HTMLDivElement>) => void;

    measuredColumnWidths: Dictionary<number>;

    reportWidthFn?: (columnKey: string, width: number) => void;

    selectionModel?: GridSelection;
    selectionTimestamp?: number;
  }

}
